// IndexedDB helper for state persistence in PWA
const DB_NAME = 'nirvanaeApp';
const DB_VERSION = 1;
const USER_STORE = 'userState';
const NOTIFICATION_STORE = 'notificationState';

// Initialize the database
export const initDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = (event) => {
      console.error('IndexedDB error:', request.error);
      reject(request.error);
    };
    
    request.onsuccess = (event) => {
      resolve(request.result);
    };
    
    request.onupgradeneeded = (event) => {
      const db = request.result;
      
      // Create stores if they don't exist
      if (!db.objectStoreNames.contains(USER_STORE)) {
        db.createObjectStore(USER_STORE, { keyPath: 'id' });
      }
      
      if (!db.objectStoreNames.contains(NOTIFICATION_STORE)) {
        db.createObjectStore(NOTIFICATION_STORE, { keyPath: 'id' });
      }
    };
  });
};

// Generic function to save data to a store
export const saveToStore = async <T>(storeName: string, data: T): Promise<void> => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      
      const request = store.put(data);
      
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
      
      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.error(`Error saving to ${storeName}:`, error);
    throw error;
  }
};

// Generic function to get data from a store
export const getFromStore = async <T>(storeName: string, key: any): Promise<T | null> => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      
      const request = store.get(key);
      
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
      
      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.error(`Error reading from ${storeName}:`, error);
    return null;
  }
};

// Specific helpers for user state
export const saveUserState = async (userData: any): Promise<void> => {
  if (!userData || !userData.id) return;
  
  await saveToStore(USER_STORE, userData);
  
  // Sync with localStorage for backward compatibility
  localStorage.setItem('userToken', JSON.stringify(userData));
  
  // Notify service worker about the state update
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'CACHE_STATE',
      payload: { type: 'user', data: userData }
    });
  }
};

export const getUserState = async (userId: number): Promise<any | null> => {
  try {
    // Try to get from IndexedDB first
    const userData = await getFromStore<any>(USER_STORE, userId);
    
    // If not in IndexedDB, try localStorage as fallback
    if (!userData) {
      const localData = localStorage.getItem('userToken');
      if (localData) {
        const parsedData = JSON.parse(localData);
        // Save to IndexedDB for future use
        if (parsedData && parsedData.id) {
          await saveToStore(USER_STORE, parsedData);
        }
        return parsedData;
      }
    }
    
    return userData;
  } catch (error) {
    console.error('Error retrieving user state:', error);
    
    // Final fallback - try localStorage
    try {
      const localData = localStorage.getItem('userToken');
      return localData ? JSON.parse(localData) : null;
    } catch {
      return null;
    }
  }
};

// Specific helpers for notification state
export const saveNotificationState = async (notificationData: any): Promise<void> => {
  if (!notificationData) return;
  
  await saveToStore(NOTIFICATION_STORE, {
    id: 'notifications',
    data: notificationData,
    timestamp: new Date().getTime()
  });
  
  // Notify service worker about the state update
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'CACHE_STATE',
      payload: { type: 'notifications', data: notificationData }
    });
  }
};

export const getNotificationState = async (): Promise<any | null> => {
  try {
    const notificationData = await getFromStore<any>(NOTIFICATION_STORE, 'notifications');
    return notificationData?.data || null;
  } catch (error) {
    console.error('Error retrieving notification state:', error);
    return null;
  }
};

// Clear all state data (for logout)
export const clearAllState = async (): Promise<void> => {
  try {
    const db = await initDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([USER_STORE, NOTIFICATION_STORE], 'readwrite');
      
      transaction.objectStore(USER_STORE).clear();
      transaction.objectStore(NOTIFICATION_STORE).clear();
      
      transaction.oncomplete = () => {
        db.close();
        // Clear localStorage as well
        localStorage.clear();
        resolve();
      };
      
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error('Error clearing state:', error);
    // Still try to clear localStorage
    localStorage.clear();
  }
};
